import React from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

// Shared building blocks for the integration "Learn more" modals, so the cloud storage and MCP
// versions stay visually identical.
//
// Vertical rhythm: sections sit 24px apart, lines within a section 8px. Nothing carries its own
// margin — spacing always comes from the surrounding flex gap.

// A block with no `subtitle` continues the one above it, so a section can pair a list of steps
// with a closing paragraph without that paragraph becoming its own heading.
export type LearnMoreContentBlock = {
  subtitle?: string;
  text?: string;
  steps?: string[];
};

/** Wraps the whole modal body: title, sections and footnote. */
export function ModalSections({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Flex column gap="24px">
      {children}
    </Flex>
  );
}

/** One section of the body — a heading with the lines that belong to it. */
export function Section({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Flex column gap="8px">
      {children}
    </Flex>
  );
}

/** Modal heading: styled as an h2, marked up as the dialog's h1. */
export function ModalTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <TitleText h2 as="h1">
      {children}
    </TitleText>
  );
}

// Leave room for the modal's absolutely positioned close button.
const TitleText = styled(Text)({ paddingRight: 32 });

export function Subtitle({ children }: { children: React.ReactNode }): JSX.Element {
  return <Text h3>{children}</Text>;
}

export function BodyText({ children }: { children: React.ReactNode }): JSX.Element {
  return <Text color="sysTextSecondary">{children}</Text>;
}

/** Renders each content block as its own section. */
export function ContentBlocks({ blocks }: { blocks: LearnMoreContentBlock[] }): JSX.Element {
  return (
    <>
      {/* Index keys: the content is a static literal, and a block's subtitle is optional. */}
      {blocks.map((block, index) => (
        <Section key={index}>
          {block.subtitle && <Subtitle>{block.subtitle}</Subtitle>}
          {block.text && <BodyText>{block.text}</BodyText>}
          {block.steps && (
            <Steps as="ol" color="sysTextSecondary">
              {block.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </Steps>
          )}
        </Section>
      ))}
    </>
  );
}

const Steps = styled(Text)({
  listStyle: "decimal",
  paddingLeft: 20,
  "& li + li": { marginTop: 8 },
});
